import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { useOpportunityStatus } from "../../../hooks/useOpportunityStatus";
import { apiService } from "../../../services/api";
import { IOpportunityStatus, CreateOpportunityStatusData } from '../../../types/opportunityStatus';
import { Offcanvas, Modal } from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { modalUtils } from '../../../utils/modalUtils';
import { ProtectedComponent } from "../../../components/ProtectedComponent";

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: IOpportunityStatus) => React.ReactNode;
  sorter?: (a: IOpportunityStatus, b: IOpportunityStatus) => number;
}

export const OpportunityStatus = () => {
  const dispatch = useDispatch();
  const { statusOportunidades, loading, error, refetchStatusOportunidades } = useOpportunityStatus();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingStatusId, setDeletingStatusId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredStatus, setFilteredStatus] = useState<typeof statusOportunidades>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Estado para controlar o status em edição
  const [editingStatus, setEditingStatus] = useState<IOpportunityStatus | null>(null);
  const [newStatus, setNewStatus] = useState<CreateOpportunityStatusData>({
    nome: '',
    descricao: ''
  });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredStatus(statusOportunidades);
  }, [statusOportunidades]);

  // Função para filtrar status quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredStatus(statusOportunidades);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');
      
      const filtered = statusOportunidades.filter(status => {
        // Campos a serem pesquisados
        const searchableFields = [
          status.nome,
          status.descricao,
          Number(status.ativo) === 1 ? 'inativo' : 'ativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term => 
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredStatus(filtered);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, statusOportunidades]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingStatus(null);
  };

  // Função para limpar o estado do novo status
  const handleCancelNew = () => {
    setNewStatus({
      nome: '',
      descricao: ''
    });
  };

  // Função para preparar a edição
  const handleEdit = (status: IOpportunityStatus) => {
    setEditingStatus(status);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (status: IOpportunityStatus) => {
    setDeletingStatusId(status.id);
  };

  const columns: TableColumn[] = [
    {
      title: "Nome",
      dataIndex: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      render: (_, record) => (
        <div>
          {record.descricao || '-'}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => {
        return (
          <div>
            {/* Garantindo que 1 (número ou string) seja Ativo */}
            {String(ativo) === "1" || ativo === 1 ? (
              <span className="badge badge-pill badge-status bg-danger">Inativo</span>
            ) : (
              <span className="badge badge-pill badge-status bg-success">Ativo</span>
            )}
          </div>
        );
      },
      sorter: (a, b) => {
        const aValue = Number(a.ativo);
        const bValue = Number(b.ativo);
        return aValue - bValue;
      },
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="OPORTUNIDADES" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit_status"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="OPORTUNIDADES" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_status"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  // Função para limpar modal
  const limparModal = (idModal: string) => {
    modalUtils.fecharModal(idModal, () => {
      if (idModal === 'success_delete_status_modal') {
        setDeletingStatusId(null);
      }
    });
  };

  // Função para criar novo status
  const handleCreateStatus = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      if (!newStatus.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }

      await apiService.createStatusOportunidade(newStatus);
      await refetchStatusOportunidades();

      handleCancelNew();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_status');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer backdrop de offcanvas e abrir modal de sucesso
      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('offcanvas-open');

        modalUtils.abrirModal('success_status_modal');
      }, 300);

    } catch (error) {
      console.error('Erro ao criar status:', error);
      alert('Erro ao criar o status. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar status
  const handleUpdateStatus = async () => {
    if (!editingStatus?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateStatusOportunidade(editingStatus.id, editingStatus);
      await refetchStatusOportunidades();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit_status');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer backdrop de offcanvas e abrir modal de sucesso
      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('offcanvas-open');

        modalUtils.abrirModal('success_edit_status_modal');
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar o status. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar status
  const handleDeleteStatus = async () => {
    if (!deletingStatusId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteStatusOportunidade(deletingStatusId);
      await refetchStatusOportunidades();

      // Fecha o modal de confirmação
      modalUtils.fecharModal('delete_status');

      // Mostra modal de sucesso
      setTimeout(() => {
        modalUtils.abrirModal('success_delete_status_modal');
      }, 400);

    } catch (error) {
      console.error('Erro ao excluir status:', error);
      alert('Erro ao excluir o status. Por favor, tente novamente.');
      
      // Garantir limpeza mesmo em caso de erro
      document.querySelectorAll('.offcanvas-backdrop, .modal-backdrop').forEach(el => el.remove());
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('offcanvas-open', 'modal-open');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Status de Oportunidade<span className="count-title">{filteredStatus.length} status</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Status"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button 
                              className="btn btn-sm position-absolute" 
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="OPORTUNIDADES" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_status"
                        >
                          <i className="ti ti-plus me-2" />
                          Adicionar Status
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  {/* Tabela de Status */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredStatus.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum status encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button 
                          className="btn btn-outline-primary mt-2" 
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table 
                        dataSource={filteredStatus.map(status => ({
                          ...status,
                          key: status.id.toString()
                        }))} 
                        columns={columns} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Novo Status */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_status">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Status</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateStatus}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={newStatus.nome}
                onChange={(e) => setNewStatus(prev => ({
                  ...prev,
                  nome: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={newStatus.descricao || ''}
                onChange={(e) => setNewStatus(prev => ({
                  ...prev,
                  descricao: e.target.value
                }))}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Offcanvas Editar Status */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit_status">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Status</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateStatus();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={editingStatus?.nome || ''}
                onChange={(e) => setEditingStatus(prev => prev ? {
                  ...prev,
                  nome: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={editingStatus?.descricao || ''}
                onChange={(e) => setEditingStatus(prev => prev ? {
                  ...prev,
                  descricao: e.target.value
                } : null)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_status" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Status?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o status selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingStatusId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteStatus}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Criação */}
      <div className="modal fade custom-modal" id="success_status_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Status Criado com Sucesso!</h3>
                <p>O status foi criado e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_status_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_status_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Status Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_edit_status_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_status_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Status Excluído com Sucesso!</h3>
                <p>O status foi removido ou desativado permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_delete_status_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};